import { useState, useEffect, useCallback, useRef } from 'react';
import html2canvas from 'html2canvas';

interface Match {
  id: number;
  team1: { name: string; score: number; logo: string; seed: number };
  team2: { name: string; score: number; logo: string; seed: number };
  winner?: 'team1' | 'team2';
  status: 'upcoming' | 'live' | 'completed';
  lastUpdated?: number;
}

interface Round {
  name: string;
  matches: Match[];
}

interface TournamentBracketProps {
  tournamentTitle: string;
  onClose: () => void;
}

const TournamentBracket = ({ tournamentTitle, onClose }: TournamentBracketProps) => {
  const [activeView, setActiveView] = useState<'winners' | 'losers'>('winners');
  const [isAutoRefresh, setIsAutoRefresh] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(Date.now());
  const [nextRefreshIn, setNextRefreshIn] = useState(10);
  const [scoreChanges, setScoreChanges] = useState<Record<string, 'team1' | 'team2' | null>>({});
  const [rounds, setRounds] = useState<Round[]>([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingMatch, setEditingMatch] = useState<{ roundName: string; matchId: number } | null>(null);
  const [editScores, setEditScores] = useState<{ team1: number; team2: number }>({ team1: 0, team2: 0 });
  const [editStatus, setEditStatus] = useState<'upcoming' | 'live' | 'completed'>('upcoming');
  const [isExporting, setIsExporting] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [exportSuccess, setExportSuccess] = useState(false);
  
  const bracketRef = useRef<HTMLDivElement>(null);
  const exportMenuRef = useRef<HTMLDivElement>(null);

  const getInitialBracketData = useCallback((): Round[] => {
    const bracketData: Record<string, Round[]> = {
      "Revenant Championship Series": [
        {
          name: "Round of 16",
          matches: [
            { id: 1, team1: { name: "Phoenix Rising", score: 2, logo: "🔥", seed: 1 }, team2: { name: "Shadow Wolves", score: 0, logo: "🐺", seed: 16 }, winner: 'team1', status: 'completed' },
            { id: 2, team1: { name: "Neon Knights", score: 2, logo: "⚡", seed: 8 }, team2: { name: "Frost Giants", score: 1, logo: "❄️", seed: 9 }, winner: 'team1', status: 'completed' },
            { id: 3, team1: { name: "Crimson Tide", score: 1, logo: "🌊", seed: 4 }, team2: { name: "Thunder Strike", score: 2, logo: "⛈️", seed: 13 }, winner: 'team2', status: 'completed' },
            { id: 4, team1: { name: "Void Walkers", score: 2, logo: "🌀", seed: 5 }, team2: { name: "Steel Legion", score: 0, logo: "🛡️", seed: 12 }, winner: 'team1', status: 'completed' },
            { id: 5, team1: { name: "Dragon Force", score: 2, logo: "🐉", seed: 2 }, team2: { name: "Night Owls", score: 1, logo: "🦉", seed: 15 }, winner: 'team1', status: 'completed' },
            { id: 6, team1: { name: "Apex Predators", score: 0, logo: "🦅", seed: 7 }, team2: { name: "Cyber Ninjas", score: 2, logo: "🥷", seed: 10 }, winner: 'team2', status: 'completed' },
            { id: 7, team1: { name: "Solar Flare", score: 2, logo: "☀️", seed: 3 }, team2: { name: "Dark Matter", score: 1, logo: "🌑", seed: 14 }, winner: 'team1', status: 'completed' },
            { id: 8, team1: { name: "Quantum Shift", score: 1, logo: "⚛️", seed: 6 }, team2: { name: "Iron Wolves", score: 2, logo: "🐺", seed: 11 }, winner: 'team2', status: 'completed' },
          ]
        },
        {
          name: "Quarter Finals",
          matches: [
            { id: 9, team1: { name: "Phoenix Rising", score: 2, logo: "🔥", seed: 1 }, team2: { name: "Neon Knights", score: 1, logo: "⚡", seed: 8 }, winner: 'team1', status: 'completed' },
            { id: 10, team1: { name: "Thunder Strike", score: 0, logo: "⛈️", seed: 13 }, team2: { name: "Void Walkers", score: 2, logo: "🌀", seed: 5 }, winner: 'team2', status: 'completed' },
            { id: 11, team1: { name: "Dragon Force", score: 1, logo: "🐉", seed: 2 }, team2: { name: "Cyber Ninjas", score: 1, logo: "🥷", seed: 10 }, status: 'live', lastUpdated: Date.now() },
            { id: 12, team1: { name: "Solar Flare", score: 0, logo: "☀️", seed: 3 }, team2: { name: "Iron Wolves", score: 0, logo: "🐺", seed: 11 }, status: 'upcoming' },
          ]
        },
        {
          name: "Semi Finals",
          matches: [
            { id: 13, team1: { name: "Phoenix Rising", score: 0, logo: "🔥", seed: 1 }, team2: { name: "Void Walkers", score: 0, logo: "🌀", seed: 5 }, status: 'upcoming' },
            { id: 14, team1: { name: "TBD", score: 0, logo: "❓", seed: 0 }, team2: { name: "TBD", score: 0, logo: "❓", seed: 0 }, status: 'upcoming' },
          ]
        },
        {
          name: "Grand Final",
          matches: [
            { id: 15, team1: { name: "TBD", score: 0, logo: "❓", seed: 0 }, team2: { name: "TBD", score: 0, logo: "❓", seed: 0 }, status: 'upcoming' },
          ]
        }
      ],
      "League Legends Cup": [
        {
          name: "Quarter Finals",
          matches: [
            { id: 1, team1: { name: "Cloud9", score: 3, logo: "☁️", seed: 1 }, team2: { name: "Evil Geniuses", score: 1, logo: "👿", seed: 8 }, winner: 'team1', status: 'completed' },
            { id: 2, team1: { name: "Team Liquid", score: 3, logo: "💧", seed: 4 }, team2: { name: "100 Thieves", score: 2, logo: "💯", seed: 5 }, winner: 'team1', status: 'completed' },
            { id: 3, team1: { name: "FlyQuest", score: 2, logo: "✈️", seed: 3 }, team2: { name: "Dignitas", score: 2, logo: "⚔️", seed: 6 }, status: 'live', lastUpdated: Date.now() },
            { id: 4, team1: { name: "TSM", score: 0, logo: "🏆", seed: 2 }, team2: { name: "Golden Guardians", score: 0, logo: "🛡️", seed: 7 }, status: 'upcoming' },
          ]
        },
        {
          name: "Semi Finals",
          matches: [
            { id: 5, team1: { name: "Cloud9", score: 0, logo: "☁️", seed: 1 }, team2: { name: "Team Liquid", score: 0, logo: "💧", seed: 4 }, status: 'upcoming' },
            { id: 6, team1: { name: "TBD", score: 0, logo: "❓", seed: 0 }, team2: { name: "TBD", score: 0, logo: "❓", seed: 0 }, status: 'upcoming' },
          ]
        },
        {
          name: "Grand Final",
          matches: [
            { id: 7, team1: { name: "TBD", score: 0, logo: "❓", seed: 0 }, team2: { name: "TBD", score: 0, logo: "❓", seed: 0 }, status: 'upcoming' },
          ]
        }
      ],
      "CS2 Elite Masters": [
        {
          name: "Quarter Finals",
          matches: [
            { id: 1, team1: { name: "Astralis", score: 2, logo: "⭐", seed: 1 }, team2: { name: "Vitality", score: 1, logo: "🐝", seed: 8 }, winner: 'team1', status: 'completed' },
            { id: 2, team1: { name: "NAVI", score: 2, logo: "🚀", seed: 4 }, team2: { name: "G2", score: 0, logo: "🎮", seed: 5 }, winner: 'team1', status: 'completed' },
            { id: 3, team1: { name: "FaZe", score: 2, logo: "🔴", seed: 3 }, team2: { name: "Heroic", score: 1, logo: "⚔️", seed: 6 }, winner: 'team1', status: 'completed' },
            { id: 4, team1: { name: "Liquid", score: 1, logo: "💧", seed: 2 }, team2: { name: "ENCE", score: 2, logo: "🦁", seed: 7 }, winner: 'team2', status: 'completed' },
          ]
        },
        {
          name: "Semi Finals",
          matches: [
            { id: 5, team1: { name: "Astralis", score: 1, logo: "⭐", seed: 1 }, team2: { name: "NAVI", score: 1, logo: "🚀", seed: 4 }, status: 'live', lastUpdated: Date.now() },
            { id: 6, team1: { name: "FaZe", score: 0, logo: "🔴", seed: 3 }, team2: { name: "ENCE", score: 0, logo: "🦁", seed: 7 }, status: 'upcoming' },
          ]
        },
        {
          name: "Grand Final",
          matches: [
            { id: 7, team1: { name: "TBD", score: 0, logo: "❓", seed: 0 }, team2: { name: "TBD", score: 0, logo: "❓", seed: 0 }, status: 'upcoming' },
          ]
        }
      ]
    };
    return bracketData[tournamentTitle] || bracketData["Revenant Championship Series"];
  }, [tournamentTitle]);

  // Initialize rounds
  useEffect(() => {
    setRounds(getInitialBracketData());
  }, [getInitialBracketData]);

  // Simulate live score updates
  const simulateLiveScoreUpdate = useCallback(() => {
    setRounds(prevRounds => {
      const newRounds = prevRounds.map(round => ({
        ...round,
        matches: round.matches.map(match => {
          if (match.status === 'live') {
            // 30% chance of score change per refresh
            const shouldUpdate = Math.random() < 0.3;
            if (shouldUpdate) {
              const scoringTeam = Math.random() < 0.5 ? 'team1' : 'team2';
              const matchKey = `${round.name}-${match.id}`;
              
              // Set score change animation
              setScoreChanges(prev => ({ ...prev, [matchKey]: scoringTeam }));
              setTimeout(() => {
                setScoreChanges(prev => ({ ...prev, [matchKey]: null }));
              }, 2000);

              return {
                ...match,
                [scoringTeam]: {
                  ...match[scoringTeam],
                  score: match[scoringTeam].score + 1
                },
                lastUpdated: Date.now()
              };
            }
          }
          return match;
        })
      }));
      return newRounds;
    });
    setLastRefresh(Date.now());
  }, []);

  // Auto-refresh countdown - pause when in edit mode
  useEffect(() => {
    if (!isAutoRefresh || isEditMode) return;

    const countdownInterval = setInterval(() => {
      setNextRefreshIn(prev => {
        if (prev <= 1) {
          simulateLiveScoreUpdate();
          return 10;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(countdownInterval);
  }, [isAutoRefresh, isEditMode, simulateLiveScoreUpdate]);

  const handleManualRefresh = () => {
    simulateLiveScoreUpdate();
    setNextRefreshIn(10);
  };

  const hasLiveMatches = rounds.some(round => 
    round.matches.some(match => match.status === 'live')
  );

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'live':
        return <span className="px-2 py-0.5 bg-red-600 text-white text-xs rounded-full animate-pulse">LIVE</span>;
      case 'completed':
        return <span className="px-2 py-0.5 bg-green-600/20 text-green-400 text-xs rounded-full">Completed</span>;
      default:
        return <span className="px-2 py-0.5 bg-gray-600/20 text-gray-400 text-xs rounded-full">Upcoming</span>;
    }
  };

  const startEditingMatch = (roundName: string, match: Match) => {
    setEditingMatch({ roundName, matchId: match.id });
    setEditScores({ team1: match.team1.score, team2: match.team2.score });
    setEditStatus(match.status);
  };

  const cancelEditing = () => {
    setEditingMatch(null);
    setEditScores({ team1: 0, team2: 0 });
    setEditStatus('upcoming');
  };

  const saveMatchScores = () => {
    if (!editingMatch) return;

    setRounds(prevRounds => {
      return prevRounds.map(round => {
        if (round.name !== editingMatch.roundName) return round;
        
        return {
          ...round,
          matches: round.matches.map(match => {
            if (match.id !== editingMatch.matchId) return match;
            
            // Determine winner based on scores if match is completed
            let winner: 'team1' | 'team2' | undefined = undefined;
            if (editStatus === 'completed') {
              if (editScores.team1 > editScores.team2) {
                winner = 'team1';
              } else if (editScores.team2 > editScores.team1) {
                winner = 'team2';
              }
            }

            const matchKey = `${round.name}-${match.id}`;
            
            // Show score change animation if scores changed
            if (editScores.team1 !== match.team1.score) {
              setScoreChanges(prev => ({ ...prev, [matchKey]: 'team1' }));
              setTimeout(() => {
                setScoreChanges(prev => ({ ...prev, [matchKey]: null }));
              }, 2000);
            }
            if (editScores.team2 !== match.team2.score) {
              setScoreChanges(prev => ({ ...prev, [matchKey]: 'team2' }));
              setTimeout(() => {
                setScoreChanges(prev => ({ ...prev, [matchKey]: null }));
              }, 2000);
            }

            return {
              ...match,
              team1: { ...match.team1, score: editScores.team1 },
              team2: { ...match.team2, score: editScores.team2 },
              status: editStatus,
              winner,
              lastUpdated: Date.now()
            };
          })
        };
      });
    });

    setLastRefresh(Date.now());
    cancelEditing();
  };

  const incrementScore = (team: 'team1' | 'team2') => {
    setEditScores(prev => ({
      ...prev,
      [team]: prev[team] + 1
    }));
  };

  const decrementScore = (team: 'team1' | 'team2') => {
    setEditScores(prev => ({
      ...prev,
      [team]: Math.max(0, prev[team] - 1)
    }));
  };

  const MatchCard = ({ match, roundName }: { match: Match; roundName: string }) => {
    const matchKey = `${roundName}-${match.id}`;
    const recentScoreChange = scoreChanges[matchKey];
    const isEditing = editingMatch?.roundName === roundName && editingMatch?.matchId === match.id;

    return (
      <div className={`bg-gray-800/80 rounded-lg border ${match.status === 'live' ? 'border-red-500 shadow-lg shadow-red-500/20' : isEditing ? 'border-yellow-500 shadow-lg shadow-yellow-500/20' : 'border-gray-700'} overflow-hidden min-w-[220px]`}>
        <div className="px-3 py-2 bg-gray-900/50 flex justify-between items-center">
          <span className="text-xs text-gray-500">Match #{match.id}</span>
          <div className="flex items-center gap-2">
            {match.status === 'live' && match.lastUpdated && !isEditing && (
              <span className="text-xs text-gray-500">
                {Math.floor((Date.now() - match.lastUpdated) / 1000)}s ago
              </span>
            )}
            {isEditing ? (
              <span className="px-2 py-0.5 bg-yellow-600 text-white text-xs rounded-full">Editing</span>
            ) : (
              getStatusBadge(match.status)
            )}
          </div>
        </div>
        
        <div className="p-2 space-y-1">
          {/* Team 1 */}
          <div className={`flex items-center justify-between p-2 rounded transition-all duration-300 ${
            match.winner === 'team1' ? 'bg-green-600/20 border border-green-500/30' : 
            match.winner === 'team2' ? 'bg-gray-700/30 opacity-60' : 'bg-gray-700/30'
          } ${recentScoreChange === 'team1' ? 'ring-2 ring-yellow-400 bg-yellow-500/20' : ''}`}>
            <div className="flex items-center gap-2">
              <span className="text-lg">{match.team1.logo}</span>
              <div>
                <p className={`text-sm font-medium ${match.winner === 'team1' ? 'text-green-400' : 'text-white'}`}>
                  {match.team1.name}
                </p>
                {match.team1.seed > 0 && (
                  <p className="text-xs text-gray-500">Seed #{match.team1.seed}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {recentScoreChange === 'team1' && !isEditing && (
                <span className="text-xs text-yellow-400 font-bold animate-bounce">+1</span>
              )}
              {isEditing ? (
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => decrementScore('team1')}
                    className="w-6 h-6 flex items-center justify-center bg-gray-700 hover:bg-gray-600 rounded text-white text-sm cursor-pointer"
                  >
                    -
                  </button>
                  <input
                    type="number"
                    value={editScores.team1}
                    onChange={(e) => setEditScores(prev => ({ ...prev, team1: Math.max(0, parseInt(e.target.value) || 0) }))}
                    className="w-12 h-8 bg-gray-900 border border-yellow-500 rounded text-center text-white text-lg font-bold"
                    min="0"
                  />
                  <button
                    onClick={() => incrementScore('team1')}
                    className="w-6 h-6 flex items-center justify-center bg-gray-700 hover:bg-gray-600 rounded text-white text-sm cursor-pointer"
                  >
                    +
                  </button>
                </div>
              ) : (
                <span className={`text-lg font-bold transition-all duration-300 ${
                  match.winner === 'team1' ? 'text-green-400' : 
                  recentScoreChange === 'team1' ? 'text-yellow-400 scale-125' : 'text-white'
                }`}>
                  {match.team1.score}
                </span>
              )}
            </div>
          </div>

          {/* VS Divider */}
          <div className="flex items-center justify-center">
            <span className="text-xs text-gray-600">VS</span>
          </div>

          {/* Team 2 */}
          <div className={`flex items-center justify-between p-2 rounded transition-all duration-300 ${
            match.winner === 'team2' ? 'bg-green-600/20 border border-green-500/30' : 
            match.winner === 'team1' ? 'bg-gray-700/30 opacity-60' : 'bg-gray-700/30'
          } ${recentScoreChange === 'team2' ? 'ring-2 ring-yellow-400 bg-yellow-500/20' : ''}`}>
            <div className="flex items-center gap-2">
              <span className="text-lg">{match.team2.logo}</span>
              <div>
                <p className={`text-sm font-medium ${match.winner === 'team2' ? 'text-green-400' : 'text-white'}`}>
                  {match.team2.name}
                </p>
                {match.team2.seed > 0 && (
                  <p className="text-xs text-gray-500">Seed #{match.team2.seed}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {recentScoreChange === 'team2' && !isEditing && (
                <span className="text-xs text-yellow-400 font-bold animate-bounce">+1</span>
              )}
              {isEditing ? (
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => decrementScore('team2')}
                    className="w-6 h-6 flex items-center justify-center bg-gray-700 hover:bg-gray-600 rounded text-white text-sm cursor-pointer"
                  >
                    -
                  </button>
                  <input
                    type="number"
                    value={editScores.team2}
                    onChange={(e) => setEditScores(prev => ({ ...prev, team2: Math.max(0, parseInt(e.target.value) || 0) }))}
                    className="w-12 h-8 bg-gray-900 border border-yellow-500 rounded text-center text-white text-lg font-bold"
                    min="0"
                  />
                  <button
                    onClick={() => incrementScore('team2')}
                    className="w-6 h-6 flex items-center justify-center bg-gray-700 hover:bg-gray-600 rounded text-white text-sm cursor-pointer"
                  >
                    +
                  </button>
                </div>
              ) : (
                <span className={`text-lg font-bold transition-all duration-300 ${
                  match.winner === 'team2' ? 'text-green-400' : 
                  recentScoreChange === 'team2' ? 'text-yellow-400 scale-125' : 'text-white'
                }`}>
                  {match.team2.score}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Edit Status Selector */}
        {isEditing && (
          <div className="px-3 py-2 bg-yellow-600/10 border-t border-yellow-500/30">
            <label className="text-xs text-gray-400 mb-1 block">Match Status</label>
            <select
              value={editStatus}
              onChange={(e) => setEditStatus(e.target.value as 'upcoming' | 'live' | 'completed')}
              className="w-full bg-gray-900 border border-gray-600 rounded px-2 py-1 text-white text-sm cursor-pointer"
            >
              <option value="upcoming">Upcoming</option>
              <option value="live">Live</option>
              <option value="completed">Completed</option>
            </select>
          </div>
        )}

        {/* Edit Actions */}
        {isEditing && (
          <div className="px-3 py-2 bg-gray-900/50 border-t border-gray-700 flex gap-2">
            <button
              onClick={saveMatchScores}
              className="flex-1 py-1.5 bg-green-600 hover:bg-green-700 text-white text-sm rounded font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-check-line mr-1"></i>
              Save
            </button>
            <button
              onClick={cancelEditing}
              className="flex-1 py-1.5 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-close-line mr-1"></i>
              Cancel
            </button>
          </div>
        )}

        {/* Edit Button (when in edit mode but not editing this match) */}
        {isEditMode && !isEditing && match.team1.name !== 'TBD' && (
          <div className="px-3 py-2 bg-gray-900/50 border-t border-gray-700">
            <button
              onClick={() => startEditingMatch(roundName, match)}
              className="w-full py-1.5 bg-yellow-600 hover:bg-yellow-700 text-white text-sm rounded font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-edit-line mr-1"></i>
              Edit Score
            </button>
          </div>
        )}

        {match.status === 'live' && !isEditing && !isEditMode && (
          <div className="px-3 py-2 bg-red-600/10 border-t border-red-500/30">
            <div className="flex items-center justify-center gap-2">
              <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
              <span className="text-xs text-red-400 font-medium">Match in Progress</span>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Close export menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(event.target as Node)) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Export bracket as image
  const exportBracket = async (action: 'download' | 'copy' | 'share') => {
    if (!bracketRef.current) return;
    
    setIsExporting(true);
    setShowExportMenu(false);
    
    try {
      const canvas = await html2canvas(bracketRef.current, {
        backgroundColor: '#111827',
        scale: 2,
        logging: false,
        useCORS: true,
        allowTaint: true,
      });
      
      const dataUrl = canvas.toDataURL('image/png');
      const fileName = `${tournamentTitle.replace(/\s+/g, '-').toLowerCase()}-bracket-${new Date().toISOString().split('T')[0]}.png`;
      
      if (action === 'download') {
        const link = document.createElement('a');
        link.download = fileName;
        link.href = dataUrl;
        link.click();
        setExportSuccess(true);
        setTimeout(() => setExportSuccess(false), 3000);
      } else if (action === 'copy') {
        const blob = await (await fetch(dataUrl)).blob();
        await navigator.clipboard.write([
          new ClipboardItem({ 'image/png': blob })
        ]);
        setExportSuccess(true);
        setTimeout(() => setExportSuccess(false), 3000);
      } else if (action === 'share') {
        const blob = await (await fetch(dataUrl)).blob();
        const file = new File([blob], fileName, { type: 'image/png' });
        
        if (navigator.share && navigator.canShare({ files: [file] })) {
          await navigator.share({
            title: `${tournamentTitle} - Tournament Bracket`,
            text: `Check out the bracket for ${tournamentTitle}!`,
            files: [file]
          });
        } else {
          // Fallback to download if share not supported
          const link = document.createElement('a');
          link.download = fileName;
          link.href = dataUrl;
          link.click();
        }
        setExportSuccess(true);
        setTimeout(() => setExportSuccess(false), 3000);
      }
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/90 z-50 overflow-auto">
      <div className="min-h-screen py-8 px-4">
        {/* Header */}
        <div className="max-w-7xl mx-auto mb-8">
          <div className="flex items-center justify-between">
            <div>
              <button 
                onClick={onClose}
                className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-4 cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                <span>Back to Tournaments</span>
              </button>
              <h1 className="text-3xl font-bold text-white">{tournamentTitle}</h1>
              <p className="text-gray-400 mt-1">Tournament Bracket</p>
            </div>
            <div className="flex items-center gap-3">
              {/* Export Button */}
              <div className="relative" ref={exportMenuRef}>
                <button
                  onClick={() => setShowExportMenu(!showExportMenu)}
                  disabled={isExporting}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    isExporting 
                      ? 'bg-gray-700 text-gray-400 cursor-not-allowed' 
                      : exportSuccess
                      ? 'bg-green-600 text-white'
                      : 'bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white'
                  }`}
                >
                  {isExporting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      Exporting...
                    </>
                  ) : exportSuccess ? (
                    <>
                      <i className="ri-check-line"></i>
                      Exported!
                    </>
                  ) : (
                    <>
                      <i className="ri-share-forward-line"></i>
                      Export Bracket
                    </>
                  )}
                </button>
                
                {/* Export Dropdown Menu */}
                {showExportMenu && (
                  <div className="absolute right-0 mt-2 w-56 bg-gray-800 rounded-xl border border-gray-700 shadow-xl overflow-hidden z-10">
                    <div className="p-2">
                      <button
                        onClick={() => exportBracket('download')}
                        className="w-full flex items-center gap-3 px-4 py-3 text-left text-white hover:bg-gray-700 rounded-lg transition-colors cursor-pointer"
                      >
                        <div className="w-10 h-10 flex items-center justify-center bg-teal-600/20 rounded-lg">
                          <i className="ri-download-2-line text-teal-400 text-lg"></i>
                        </div>
                        <div>
                          <p className="font-medium">Download Image</p>
                          <p className="text-xs text-gray-400">Save as PNG file</p>
                        </div>
                      </button>
                      
                      <button
                        onClick={() => exportBracket('copy')}
                        className="w-full flex items-center gap-3 px-4 py-3 text-left text-white hover:bg-gray-700 rounded-lg transition-colors cursor-pointer"
                      >
                        <div className="w-10 h-10 flex items-center justify-center bg-cyan-600/20 rounded-lg">
                          <i className="ri-file-copy-line text-cyan-400 text-lg"></i>
                        </div>
                        <div>
                          <p className="font-medium">Copy to Clipboard</p>
                          <p className="text-xs text-gray-400">Paste anywhere</p>
                        </div>
                      </button>
                      
                      <button
                        onClick={() => exportBracket('share')}
                        className="w-full flex items-center gap-3 px-4 py-3 text-left text-white hover:bg-gray-700 rounded-lg transition-colors cursor-pointer"
                      >
                        <div className="w-10 h-10 flex items-center justify-center bg-purple-600/20 rounded-lg">
                          <i className="ri-share-line text-purple-400 text-lg"></i>
                        </div>
                        <div>
                          <p className="font-medium">Share</p>
                          <p className="text-xs text-gray-400">Share via apps</p>
                        </div>
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Edit Mode Toggle */}
              <button
                onClick={() => {
                  setIsEditMode(!isEditMode);
                  if (isEditMode) {
                    cancelEditing();
                  }
                }}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  isEditMode 
                    ? 'bg-yellow-600 text-white' 
                    : 'bg-gray-800 text-gray-400 hover:text-white hover:bg-gray-700'
                }`}
              >
                <i className={isEditMode ? "ri-close-line" : "ri-edit-line"}></i>
                {isEditMode ? 'Exit Edit Mode' : 'Edit Scores'}
              </button>
              <button 
                onClick={onClose}
                className="w-10 h-10 flex items-center justify-center bg-gray-800 hover:bg-gray-700 rounded-full text-gray-400 hover:text-white transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
          </div>

          {/* Edit Mode Banner */}
          {isEditMode && (
            <div className="mt-6 p-4 bg-gradient-to-r from-yellow-900/30 to-orange-900/30 rounded-xl border border-yellow-500/30">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center bg-yellow-600 rounded-full">
                  <i className="ri-edit-line text-white text-lg"></i>
                </div>
                <div>
                  <h3 className="text-white font-semibold">Edit Mode Active</h3>
                  <p className="text-gray-400 text-sm">Click "Edit Score" on any match to update scores and status manually</p>
                </div>
              </div>
            </div>
          )}

          {/* Live Score Controls */}
          {hasLiveMatches && !isEditMode && (
            <div className="mt-6 p-4 bg-gradient-to-r from-red-900/30 to-orange-900/30 rounded-xl border border-red-500/30">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-red-600 rounded-full animate-pulse">
                    <i className="ri-live-line text-white text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-white font-semibold">Live Score Updates</h3>
                    <p className="text-gray-400 text-sm">Scores refresh automatically during live matches</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  {/* Auto-refresh toggle */}
                  <div className="flex items-center gap-3">
                    <span className="text-gray-400 text-sm">Auto-refresh</span>
                    <button
                      onClick={() => setIsAutoRefresh(!isAutoRefresh)}
                      className={`relative w-12 h-6 rounded-full transition-colors cursor-pointer ${
                        isAutoRefresh ? 'bg-green-600' : 'bg-gray-600'
                      }`}
                    >
                      <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                        isAutoRefresh ? 'left-7' : 'left-1'
                      }`}></span>
                    </button>
                  </div>

                  {/* Countdown / Manual refresh */}
                  {isAutoRefresh ? (
                    <div className="flex items-center gap-2 px-4 py-2 bg-gray-800/50 rounded-lg">
                      <i className="ri-refresh-line text-green-400"></i>
                      <span className="text-white text-sm">Next update in</span>
                      <span className="text-green-400 font-bold text-lg w-6 text-center">{nextRefreshIn}</span>
                      <span className="text-white text-sm">s</span>
                    </div>
                  ) : (
                    <button
                      onClick={handleManualRefresh}
                      className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-refresh-line"></i>
                      Refresh Now
                    </button>
                  )}

                  {/* Last updated */}
                  <div className="text-gray-500 text-xs">
                    Last updated: {new Date(lastRefresh).toLocaleTimeString()}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* View Toggle */}
          <div className="flex gap-2 mt-6">
            <button
              onClick={() => setActiveView('winners')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors cursor-pointer whitespace-nowrap ${
                activeView === 'winners' 
                  ? 'bg-purple-600 text-white' 
                  : 'bg-gray-800 text-gray-400 hover:text-white'
              }`}
            >
              <i className="ri-trophy-line mr-2"></i>
              Winners Bracket
            </button>
            <button
              onClick={() => setActiveView('losers')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors cursor-pointer whitespace-nowrap ${
                activeView === 'losers' 
                  ? 'bg-purple-600 text-white' 
                  : 'bg-gray-800 text-gray-400 hover:text-white'
              }`}
            >
              <i className="ri-shield-cross-line mr-2"></i>
              Losers Bracket
            </button>
          </div>
        </div>

        {/* Bracket Visualization */}
        <div className="max-w-7xl mx-auto" ref={bracketRef}>
          {/* Export Header - Only visible in export */}
          <div className="hidden export-header mb-6 p-4 bg-gradient-to-r from-gray-900 to-gray-800 rounded-xl border border-gray-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-teal-500 to-cyan-600 rounded-full">
                  <i className="ri-trophy-line text-white text-2xl"></i>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">{tournamentTitle}</h2>
                  <p className="text-gray-400 text-sm">Tournament Bracket • {new Date().toLocaleDateString()}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-gray-500 text-xs">Generated by</p>
                <p className="text-teal-400 font-semibold">Revenant Gaming</p>
              </div>
            </div>
          </div>

          {activeView === 'winners' ? (
            <div className="overflow-x-auto pb-8">
              <div className="flex gap-8 min-w-max">
                {rounds.map((round, roundIndex) => (
                  <div key={round.name} className="flex flex-col">
                    {/* Round Header */}
                    <div className="text-center mb-6">
                      <h3 className="text-lg font-bold text-white">{round.name}</h3>
                      <p className="text-sm text-gray-500">{round.matches.length} {round.matches.length === 1 ? 'Match' : 'Matches'}</p>
                      {round.matches.some(m => m.status === 'live') && (
                        <span className="inline-flex items-center gap-1 mt-2 px-2 py-1 bg-red-600/20 text-red-400 text-xs rounded-full">
                          <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse"></span>
                          Live matches
                        </span>
                      )}
                    </div>

                    {/* Matches */}
                    <div 
                      className="flex flex-col gap-4 justify-around flex-1"
                      style={{ 
                        paddingTop: roundIndex > 0 ? `${Math.pow(2, roundIndex) * 20}px` : '0',
                        gap: roundIndex > 0 ? `${Math.pow(2, roundIndex) * 40}px` : '16px'
                      }}
                    >
                      {round.matches.map((match) => (
                        <div key={match.id} className="relative">
                          <MatchCard match={match} roundName={round.name} />
                          
                          {/* Connector Lines */}
                          {roundIndex < rounds.length - 1 && (
                            <div className="absolute top-1/2 -right-8 w-8 h-px bg-gray-700"></div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}

                {/* Champion Trophy */}
                <div className="flex flex-col items-center justify-center min-w-[150px]">
                  <div className="text-center">
                    <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-yellow-500 to-amber-600 rounded-full mb-4 shadow-lg shadow-yellow-500/30">
                      <i className="ri-trophy-fill text-4xl text-white"></i>
                    </div>
                    <h3 className="text-lg font-bold text-yellow-400">Champion</h3>
                    <p className="text-sm text-gray-500">TBD</p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-gray-800 rounded-full mx-auto mb-4">
                  <i className="ri-shield-cross-line text-3xl text-gray-600"></i>
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Losers Bracket</h3>
                <p className="text-gray-400">Coming soon - matches will appear as teams are eliminated</p>
              </div>
            </div>
          )}
        </div>

        {/* Legend */}
        <div className="max-w-7xl mx-auto mt-8 pt-8 border-t border-gray-800">
          <div className="flex flex-wrap gap-6 justify-center">
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></span>
              <span className="text-sm text-gray-400">Live Match</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 bg-green-500 rounded-full"></span>
              <span className="text-sm text-gray-400">Completed</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 bg-gray-500 rounded-full"></span>
              <span className="text-sm text-gray-400">Upcoming</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="px-2 py-0.5 bg-green-600/20 border border-green-500/30 rounded text-xs text-green-400">Winner</div>
              <span className="text-sm text-gray-400">Match Winner</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="px-2 py-0.5 bg-yellow-500/20 border border-yellow-400/30 rounded text-xs text-yellow-400">+1</div>
              <span className="text-sm text-gray-400">Score Update</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="px-2 py-0.5 bg-yellow-600/20 border border-yellow-500/30 rounded text-xs text-yellow-400">Edit</div>
              <span className="text-sm text-gray-400">Editing Match</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TournamentBracket;
